import { useEffect, useRef, useState } from "react";
import { BriefcaseBusiness, ChartLine, CircleCheck, Lightbulb, Search, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import SectionHead from "../SectionHead/SectionHead";
import "./HowWeWork.css";

// ícones na mesma ordem de home.process.steps nos locales
const ICONS = [Search, Settings, CircleCheck, BriefcaseBusiness, ChartLine];

type Step = { title: string; text: string };

const pad = (n: number) => String(n).padStart(2, "0");

// só anima se der para saber quando a seção entra na tela e se o usuário não pediu menos movimento
const canAnimate = () =>
  typeof window !== "undefined" &&
  "IntersectionObserver" in window &&
  !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Como trabalhamos: quando a seção aparece, a linha corre de etapa em etapa e cada uma surge quando a linha chega
// nela (os atrasos ficam no CSS, a partir de --i).
export default function HowWeWork() {
  const { t } = useTranslation();
  const raw = t("home.process.steps", { returnObjects: true });
  const steps = Array.isArray(raw) ? (raw as Step[]) : [];
  const ref = useRef<HTMLOListElement>(null);
  const [animate] = useState(canAnimate);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!animate || !el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setVisible(true);
        io.disconnect();
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animate]);

  const state = animate ? ` is-animated${visible ? " is-visible" : ""}` : "";

  return (
    <section
      className="cz-how-section"
      aria-labelledby="cz-how-title"
      style={{ "--cz-how-count": steps.length } as React.CSSProperties}
    >
      <div className="cz-how-section__inner">
        <SectionHead id="cz-how-title" title={t("home.process.title")} text={t("home.process.text")} />

        <ol ref={ref} className={`cz-how${state}`}>
          {steps.map(({ title, text }, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <li
                key={title}
                className={`cz-how__step${i === steps.length - 1 ? " is-last" : ""}`}
                style={{ "--i": i } as React.CSSProperties}
              >
                {i < steps.length - 1 && <span className="cz-how__line" aria-hidden="true" />}
                <span className="cz-how__dot" aria-hidden="true">
                  <Icon size={18} strokeWidth={1.8} />
                </span>
                <div className="cz-how__card">
                  <span className="cz-how__num">{pad(i + 1)}</span>
                  <h3 className="cz-how__title">{title}</h3>
                  <p className="cz-how__text">{text}</p>
                </div>
              </li>
            );
          })}
        </ol>

        <p className={`cz-how__tip${state}`}>
          <Lightbulb size={18} strokeWidth={1.8} aria-hidden="true" />
          <span>{t("home.process.tip")}</span>
        </p>
      </div>
    </section>
  );
}
